import { Component, computed, inject, LOCALE_ID, signal } from '@angular/core';
import { ProfileService } from '../../../../services/profile-service';
import { CurrentUserService } from '../../../../services/current-user.service';
import { Router } from '@angular/router';
import { finalize } from 'rxjs';
import { LanguageService } from '../../../../services/language.service';

@Component({
  selector: 'app-delete-account',
  imports: [],
  templateUrl: './delete-account.html',
  styleUrl: './delete-account.scss'
})
export class DeleteAccount {
  private router = inject(Router);
  private profileService = inject(ProfileService);
  private currentUserService = inject(CurrentUserService);
  private locale = inject(LOCALE_ID);


  loading = signal(false);
  apiError = signal('');
  isAr = computed(() => this.locale.startsWith('ar'));

checklist = [
  {
    id: 'data-deactivation',
    title: 'Data Deactivation',
    titleAr: 'تعطيل الحساب',
    description: 'Your account will be temporarily deactivated for 30 days. Your login credentials and any authentication tokens will also be removed.',
    descAr: 'سيتم تعطيل حسابك مؤقتًا لمدة 30 يومًا. سيتم أيضًا إزالة بيانات اعتماد تسجيل الدخول الخاصة بك وأي رموز مصادقة.',
    checked: false,
  },
  {
    id: 'device-removal',
    title: 'Device Removal',
    titleAr: 'إزالة الأجهزة',
    description: 'All devices registered under your account will be disassociated and deleted. Any device configurations, logs, or historical data will be permanently erased.',
    descAr: 'سيتم فك ارتباط وحذف جميع الأجهزة المسجلة تحت حسابك. سيتم مسح أي إعدادات للأجهزة أو سجلات أو بيانات تاريخية نهائيًا.',
    checked: false,
  },
  {
    id: 'data-loss',
    title: 'Data Loss',
    titleAr: 'فقدان البيانات',
    description: 'All user-generated data (including sensor readings, automations, settings, and preferences) will be irretrievable after deletion. If your account is linked to third-party integrations, those connections will be revoked.',
    descAr: 'جميع البيانات التي أنشأها المستخدم (بما في ذلك قراءات المستشعرات، والأتمتة، والإعدادات، والتفضيلات) لا يمكن استردادها بعد الحذف. إذا كان حسابك مرتبطًا بتطبيقات خارجية، فسيتم إلغاء تلك الارتباطات.',
    checked: false,
  },
  {
    id: 'data-removal',
    title: 'Data Removal',
    titleAr: 'حذف البيانات الشخصية',
    description: 'All personal information associated with your account (such as name, email, and contact details) will be permanently deleted from our system. After 30 days, your account and all related data will be permanently deleted.',
    descAr: 'سيتم حذف جميع المعلومات الشخصية المرتبطة بحسابك (مثل الاسم والبريد الإلكتروني وتفاصيل الاتصال) نهائيًا من نظامنا. بعد 30 يومًا، سيتم حذف حسابك وجميع البيانات المتعلقة به نهائيًا.',
    checked: false,
  },
  {
    id: 'irreversibility',
    title: 'Irreversibility',
    titleAr: 'إجراء غير قابل للتراجع',
    description: 'This action is permanent and cannot be undone. You will need to create a new account to use the platform again.',
    descAr: 'هذا الإجراء نهائي ولا يمكن التراجع عنه. ستحتاج إلى إنشاء حساب جديد لاستخدام المنصة مرة أخرى.',
    checked: false,
  },
];

  get allChecked(): boolean {
    return this.checklist.every((item) => item.checked);
  }

  toggleCheck(index: number) {
    this.checklist[index].checked = !this.checklist[index].checked;
  }

  private get profileBasePath(): string {
    return this.router.url.includes('/admin') ? '/admin/profile' : '/user/profile';
  }

  onCancel() {
    this.router.navigate([this.profileBasePath]);
  }

  onSave() {
    if (this.allChecked) {
      this.apiError.set('');
      this.loading.set(true);
      this.profileService
        .deleteUser()
        .pipe(finalize(() => this.loading.set(false)))
        .subscribe({
          next: () => {
            console.log('Account deleted successfully');
            this.currentUserService.logout();
            this.router.navigate(['/login']);
          },
          error: (err) => {
            console.error('Failed to delete account', err);
            this.apiError.set(
              err.messageEn || 'An unexpected error occurred while deleting account. Please try again later.',
            );
          },
        });
    }
  }
}

